import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth";
import { useDepositoEscolhido } from "../../lib/consultas";
import { supabase } from "../../lib/supabase";
import { useTempoReal } from "../../lib/tempoReal";
import { mensagemDeErro, rpc } from "../../lib/rpc";
import { chamarFuncao } from "../../lib/funcoes";
import { sinalErro, sinalOk } from "../../lib/sinais";
import { fmtDataHora, fmtNum } from "../../lib/formato";
import { LeituraCodigo } from "../../componentes/LeituraCodigo";
import { AlertaErro } from "../../componentes/Alerta";
import { Botao, Campo, Escolha, MensagemErro, Vazio } from "../../componentes/ui";
import {
  type LinhaDevolucao, MOTIVOS, nomeOrigemDevolucao, type NotaVendaTiny, type OrigemDevolucao, type ResumoDevolucao,
} from "./tipos";

type BuscaVenda = { ok: boolean; nota?: NotaVendaTiny; notas?: Array<{ id: number; numero: string | null; serie: string | null; emitida_em: string | null }> };

/** Doca: a caixa de devolução chegou. Bipa o rastreio, o pedido ou o DEV-nº e vai para a conferência. */
export default function ReceberDevolucao() {
  const { perfil } = useAuth();
  const navegar = useNavigate();
  const { deposito } = useDepositoEscolhido(perfil?.deposito_padrao_id);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [tremor, setTremor] = useState(0);
  const [semPedido, setSemPedido] = useState(false);
  const [buscandoTiny, setBuscandoTiny] = useState<string | null>(null);
  const [venda, setVenda] = useState<NotaVendaTiny | null>(null);
  const [opcoes, setOpcoes] = useState<{ codigo: string; notas: NonNullable<BuscaVenda["notas"]> } | null>(null);

  const aguardando = useQuery({
    queryKey: ["devolucoes", "aguardando"],
    queryFn: async () => {
      const { data, error } = await supabase.from("v_devolucoes").select("*").in("status", ["aguardando", "em_conferencia"])
        .order("criado_em").limit(50);
      if (error) throw error;
      return data as LinhaDevolucao[];
    },
    refetchInterval: 60_000,
  });
  useTempoReal(["devolucoes"], [["devolucoes", "aguardando"]]);

  async function receber(codigo: string) {
    setOcupado(true);
    try {
      const r = await rpc<ResumoDevolucao>("wms_devolucao_receber", { p_codigo: codigo, p_deposito_id: deposito });
      if (!r.ok && r.codigo_erro === "NAO_ENCONTRADA") return void buscarNoTiny(codigo);
      if (!r.ok) {
        sinalErro();
        setTremor((n) => n + 1);
        setErro(r.mensagem ?? "Devolução não encontrada");
        return;
      }
      sinalOk();
      navegar(`/devolucoes/${r.devolucao.id}`);
    } catch (e) {
      sinalErro();
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  // Não está no WMS: procura a nota de venda no Tiny (chave do DANFE ou número da NF)
  async function buscarNoTiny(codigo: string, id?: number) {
    setBuscandoTiny(codigo);
    setOpcoes(null);
    try {
      const r = await chamarFuncao<BuscaVenda>("tiny-notas", id ? { venda: codigo, id } : { venda: codigo });
      if (r.nota) {
        sinalOk();
        setVenda(r.nota);
      } else if (r.notas?.length) {
        setOpcoes({ codigo, notas: r.notas });
      }
    } catch (e) {
      sinalErro();
      setTremor((n) => n + 1);
      setErro(`Não achei "${codigo}" no WMS nem no Tiny (${mensagemDeErro(e)}). Bipe o código de barras do DANFE, digite o número da NF ou do pedido, ou abra sem pedido.`);
    } finally {
      setBuscandoTiny(null);
    }
  }

  const emConferencia = (aguardando.data ?? []).filter((d) => d.status === "em_conferencia");
  const esperando = (aguardando.data ?? []).filter((d) => d.status === "aguardando");

  return (
    <div className="flex flex-col gap-3">
      <AlertaErro mensagem={erro} onFechar={() => setErro(null)} />
      <h1 className="font-sinal text-3xl font-bold">Receber devolução</h1>
      {!semPedido && !venda && (
        <LeituraCodigo rotulo="Bipe o rastreio, o pedido, a chave do DANFE ou o DEV-nº" onLeitura={(c) => void receber(c)}
          desabilitado={ocupado || !!buscandoTiny} erro={tremor} />
      )}
      <p className="text-sm text-aco-escuro">
        Caixa sem aviso do CX: bipe o pedido, o rastreio ou o código de barras do DANFE e a devolução abre aqui mesmo. Venda que não
        passou pelo WMS é procurada no Tiny pela chave do DANFE, pelo número da NF, do pedido ou do marketplace.
      </p>
      {buscandoTiny && <p className="rounded-md bg-info-claro px-3 py-2 font-semibold text-info" role="status">Procurando a venda {buscandoTiny} no Tiny...</p>}
      {opcoes && (
        <section className="flex flex-col gap-2 rounded-lg border-2 border-tinta bg-chapa p-4">
          <h2 className="font-sinal text-xl font-semibold">Mais de uma nota com o número {opcoes.codigo}</h2>
          {opcoes.notas.map((n) => (
            <Botao key={n.id} variante="secundario" onClick={() => void buscarNoTiny(opcoes.codigo, n.id)}>
              NF {n.numero} série {n.serie}{n.emitida_em ? ` de ${fmtDataHora(n.emitida_em)}` : ""}
            </Botao>
          ))}
          <Botao variante="fantasma" onClick={() => setOpcoes(null)}>Cancelar</Botao>
        </section>
      )}
      {venda && <AbrirPelaVenda venda={venda} deposito={deposito} onAberta={(id) => navegar(`/devolucoes/${id}`)} onFechar={() => setVenda(null)} />}

      {semPedido
        ? <AbrirSemPedido deposito={deposito} onAberta={(id) => navegar(`/devolucoes/${id}`)} onFechar={() => setSemPedido(false)} />
        : <Botao variante="secundario" onClick={() => setSemPedido(true)}>Chegou sem pedido (Full ou transportadora)</Botao>}

      {emConferencia.length > 0 && (
        <section>
          <h2 className="mb-2 font-sinal text-xl font-semibold">Em conferência</h2>
          <ul className="flex flex-col gap-2">
            {emConferencia.map((d) => (
              <li key={d.id}>
                <Link to={`/devolucoes/${d.id}`} className="flex min-h-14 items-center justify-between gap-3 rounded-lg border-2 border-tinta bg-faixa-clara px-4 py-2">
                  <span><span className="font-sinal text-2xl font-bold">DEV-{d.numero}</span> <span className="block text-sm">{d.pedido ? `Pedido ${d.pedido}` : d.nota_venda ? `NF ${d.nota_venda}` : nomeOrigemDevolucao[d.origem]} · {d.motivo}</span></span>
                  <span className="font-semibold">Continuar</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 font-sinal text-xl font-semibold">Avisadas pelo CX</h2>
        {esperando.length ? (
          <ul className="flex flex-col gap-2">
            {esperando.map((d) => (
              <li key={d.id}>
                <button type="button" disabled={ocupado} onClick={() => void receber(`DEV-${d.numero}`)}
                  className="flex min-h-14 w-full items-center justify-between gap-3 rounded-lg border-2 border-aco bg-chapa px-4 py-2 text-left">
                  <span>
                    <span className="font-sinal text-2xl font-bold">DEV-{d.numero}</span>
                    <span className="block text-sm">{d.pedido ? `Pedido ${d.pedido}` : d.nota_venda ? `NF ${d.nota_venda}` : nomeOrigemDevolucao[d.origem]} · {d.motivo}</span>
                    <span className="block text-xs text-aco-escuro">{d.codigo_rastreio ? `Rastreio ${d.codigo_rastreio} · ` : ""}avisada em {fmtDataHora(d.criado_em)}</span>
                  </span>
                  <span className="font-semibold">Chegou</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <Vazio>{aguardando.isLoading ? "Carregando..." : "Nenhuma devolução avisada."}</Vazio>}
      </section>
      <Link to="/devolucoes" className="text-center font-semibold underline">Todas as devoluções</Link>
    </div>
  );
}

/** Venda achada no Tiny: confere a nota e abre a devolução com os itens dela. */
function AbrirPelaVenda({ venda, deposito, onAberta, onFechar }: {
  venda: NotaVendaTiny; deposito: string | null; onAberta: (id: string) => void; onFechar: () => void;
}) {
  const [motivo, setMotivo] = useState("Chegou sem aviso do CX");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function abrir() {
    setOcupado(true);
    setErro(null);
    try {
      const r = await rpc<ResumoDevolucao>("wms_devolucao_abrir_por_nota", { p_nota: { ...venda, motivo: motivo.trim() }, p_deposito_id: deposito });
      if (!r.ok) return setErro(r.mensagem ?? "Não foi possível abrir");
      onAberta(r.devolucao.id);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section data-dialogo-aberto className="flex flex-col gap-3 rounded-lg border-2 border-tinta bg-chapa p-4">
      <h2 className="font-sinal text-2xl font-bold">Venda achada no Tiny: NF {venda.numero}{venda.serie ? ` série ${venda.serie}` : ""}</h2>
      <p className="text-aco-escuro">
        {venda.emitida_em ? `Emitida em ${fmtDataHora(venda.emitida_em)}` : ""}{venda.pedido_numero ? ` · pedido ${venda.pedido_numero}` : ""}.
        O pedido não passou pelo WMS: a devolução abre com os itens desta nota.
      </p>
      <ul className="rounded-md bg-concreto px-3 py-2">
        {venda.itens.map((i) => <li key={i.numero_item}><strong>{fmtNum(i.quantidade)}x</strong> {i.codigo} {i.descricao}</li>)}
      </ul>
      <Campo rotulo="Motivo" list="motivos-venda" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <datalist id="motivos-venda">{["Chegou sem aviso do CX", ...MOTIVOS].map((m) => <option key={m} value={m} />)}</datalist>
      <MensagemErro>{erro}</MensagemErro>
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={onFechar}>Não é esta</Botao>
        <Botao className="flex-1" disabled={ocupado || !motivo.trim()} onClick={() => void abrir()}>Abrir devolução e conferir</Botao>
      </div>
    </section>
  );
}

function AbrirSemPedido({ deposito, onAberta, onFechar }: { deposito: string | null; onAberta: (id: string) => void; onFechar: () => void }) {
  const [origem, setOrigem] = useState<OrigemDevolucao>("FULL");
  const [motivo, setMotivo] = useState("");
  const [rastreio, setRastreio] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function abrir() {
    if (!motivo.trim()) return setErro("Informe o motivo");
    setOcupado(true);
    setErro(null);
    try {
      const r = await rpc<ResumoDevolucao>("wms_devolucao_abrir_sem_pedido", {
        p_origem: origem, p_motivo: motivo.trim(), p_codigo_rastreio: rastreio.trim() || null, p_deposito_id: deposito,
      });
      if (!r.ok) return setErro(r.mensagem ?? "Não foi possível abrir");
      onAberta(r.devolucao.id);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section data-dialogo-aberto className="flex flex-col gap-3 rounded-lg border-2 border-tinta bg-chapa p-4">
      <h2 className="font-sinal text-xl font-semibold">Devolução sem pedido</h2>
      <Escolha rotulo="De onde veio" value={origem} onChange={(e) => setOrigem(e.target.value as OrigemDevolucao)}>
        <option value="FULL">{nomeOrigemDevolucao.FULL}</option>
        <option value="TRANSPORTADORA">{nomeOrigemDevolucao.TRANSPORTADORA}</option>
      </Escolha>
      <Campo rotulo="Motivo" list="motivos-sem-pedido" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <datalist id="motivos-sem-pedido">{[...MOTIVOS, "Retirada do estoque Full", "Extravio devolvido"].map((m) => <option key={m} value={m} />)}</datalist>
      <Campo rotulo="Rastreio ou número da remessa (opcional)" value={rastreio} onChange={(e) => setRastreio(e.target.value)} />
      <MensagemErro>{erro}</MensagemErro>
      <div className="flex gap-2">
        <Botao variante="secundario" onClick={onFechar}>Voltar</Botao>
        <Botao className="flex-1" disabled={ocupado} onClick={() => void abrir()}>Abrir e conferir</Botao>
      </div>
    </section>
  );
}
